import { Component, OnChanges, OnInit, SimpleChanges, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { Subscription } from 'rxjs';
import { DiagnosticQuestion, QuizOptionBlock, RichContentBlock } from 'src/app/shared/models/diagnosis.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { DiagnosticQuestionsStore } from 'src/app/shared/services/stores/diagnostic-questions.store.service';
import { DeleteConfirmationDialog } from 'src/app/shared/components/delete-confirmation/delete-confirmation.component';
import { VALID_CEFRS } from 'src/app/shared/constants';
import { defaultImageUrl } from 'src/app/shared/utils/diagnostic-asset.util';

@Component({
	selector: 'app-diagnostic-questions',
	templateUrl: './diagnostic-questions.component.html',
	styleUrls: ['./diagnostic-questions.component.scss']
})
export class DiagnosticQuestionsComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

	questions: DiagnosticQuestion[] = [];
	loading = true;

	cefrs = VALID_CEFRS;
	readonly fallbackImageUrl = defaultImageUrl();

	cefrControl: FormControl;
	statusControl: FormControl;

	globalfilter = '';
	filteredValues = {
		cefr: '',
		status: null,
	}

	displayedColumns: string[] = ['select', 'name', 'cefr', 'published', 'actions'];
	dataSource!: MatTableDataSource<DiagnosticQuestion>;
	questionsSelected: { [key: string]: boolean } = {};
	selected: string[] = [];
	defaultPageSize!: number;

	selectedQuestionTab: { [key: string]: string } = {};

	@ViewChild(MatPaginator) paginator!: MatPaginator;

	subscriptions: Subscription;

	constructor(
		private dialog: MatDialog,
		private router: Router,
		private diagnosticQuestionsStore: DiagnosticQuestionsStore,
		private authService: AuthService,
		private toastService: ToastService,
		private clipboard: Clipboard,
	) {
		this.dataSource = new MatTableDataSource(this.questions);
		this.cefrControl = new FormControl();
		this.statusControl = new FormControl();
		this.subscriptions = new Subscription();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.questions) {
			this.dataSource = new MatTableDataSource(this.questions);
			this.dataSource.filterPredicate = this.customFilterPredicate();
			this.globalfilter = sessionStorage.getItem('diagnosticQuestionFilter') ?? '';
			this.defaultPageSize = +(sessionStorage.getItem('diagnosticQuestionsPageSize') ?? 10);
		}
	}

	ngOnInit() {
		this.loadQuestions();

		this.globalfilter = sessionStorage.getItem('diagnosticQuestionFilter') ?? '';
		this.defaultPageSize = +(sessionStorage.getItem('diagnosticQuestionsPageSize') ?? 10);

		const cefrSub = this.cefrControl.valueChanges.subscribe(cefrValue => {
			this.filteredValues['cefr'] = cefrValue;
			this.dataSource.filter = JSON.stringify(this.filteredValues);
		});

		const statusSub = this.statusControl.valueChanges.subscribe(statusValue => {
			this.filteredValues['status'] = statusValue;
			this.dataSource.filter = JSON.stringify(this.filteredValues);
		});

		this.subscriptions.add(cefrSub);
		this.subscriptions.add(statusSub);
	}

	ngAfterViewInit(): void {
		this.setPaginator();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	loadQuestions(forceUpdate: boolean = false) {
		return new Promise(() => {
			const sub = this.diagnosticQuestionsStore.getQuestions(forceUpdate)
				.subscribe(res => {
					if (res) {
						this.questions = res;
						this.dataSource = new MatTableDataSource(this.questions);
						this.questions.forEach(question => { this.questionsSelected[question.uid ?? 0] = false; });
						this.dataSource.filterPredicate = this.customFilterPredicate();
						if (this.paginator) {
							this.setPaginator();
						}
						this.loading = false;
					}
				});

			this.subscriptions.add(sub);
		});
	}

	private setPaginator() {
		this.dataSource.filter = JSON.stringify(this.filteredValues);
		this.dataSource.paginator = this.paginator;
		this.paginator.pageIndex = +(sessionStorage.getItem('diagnosticQuestionPageIndex') ?? 0);
	}

	home() {
		this.router.navigate(['navbar'])
	}

	async logout() {
		let data = await this.authService.logout();
		if (data) {
			this.router.navigate(['auth/login'])
		}
	}

	selectAll(event: MatCheckboxChange) {
		for (let questionId in this.questionsSelected) {
			this.questionsSelected[questionId] = event.checked;
		}
	}

	select(event: MatCheckboxChange, checkedId: string) {
		this.questionsSelected[checkedId] = event.checked;
	}

	customFilterPredicate() {
		const questionFilter = (data: DiagnosticQuestion, filter: string): boolean => {
			let parsedSearch = JSON.parse(filter);

			const selectedCefr = parsedSearch.cefr;
			let hasCefr = selectedCefr ? data.cefr === selectedCefr : true;

			const selectedStatus = parsedSearch.status;
			const hasStatus = selectedStatus === null ? true : data.published === selectedStatus;

			let selectMatch = hasCefr && hasStatus;
			let globalMatch = !this.globalfilter;

			if (this.globalfilter) {
				const lowerCaseFilter = this.globalfilter.toLowerCase();
				const nameIncludes = data.name != null && data.name.toLowerCase().includes(lowerCaseFilter);
				globalMatch = nameIncludes;
			}

			return globalMatch && selectMatch;
		}
		return questionFilter;
	}

	applyFilter(filter: string) {
		sessionStorage.setItem('diagnosticQuestionFilter', filter);
		this.globalfilter = filter;
		this.dataSource.filter = JSON.stringify(this.filteredValues);

		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	paginatorChanged(event: any) {
		sessionStorage.setItem('diagnosticQuestionPageIndex', event.pageIndex);
		sessionStorage.setItem('diagnosticQuestionsPageSize', event.pageSize);
	}

	copyToClipboard(text: string): void {
		this.clipboard.copy(text);
		this.toastService.success(`Question ID copied to clipboard`);
	}

	createQuestion() {
		this.router.navigate(['/navbar/diagnostic-questions/create']);
	}

	editQuestion(question: DiagnosticQuestion) {
		this.router.navigate(['/navbar/diagnostic-questions', question.uid, 'edit']);
	}

	private getSelected() {
		const selectedIds = [];

		for (let id in this.questionsSelected) {
			const isFiltered = this.dataSource.filteredData.find(question => question.uid === id);
			if (this.questionsSelected[id] && isFiltered) {
				selectedIds.push(id);
			}
		}

		return selectedIds;
	}

	removeSelectedQuestions() {
		const selectedIds = this.getSelected();
		if (selectedIds.length === 0) {
			this.toastService.error(`You must select at least a single question to delete!`);
			return;
		}

		if (selectedIds.length > 5) {
			this.toastService.error(`You can only delete 5 questions at a time!`);
			return;
		}

		const dialogRef = this.dialog.open(DeleteConfirmationDialog, {
			width: "350px",
		});

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (!result) {
				return;
			}

			selectedIds.forEach((id: string) => {
				this.diagnosticQuestionsStore.removeQuestion(id);
				this.loadQuestions(true);
			});
		});
	}

	publishQuestions(release: boolean) {
		let selectedIds = this.getSelected();
		if (selectedIds.length === 0) {
			this.toastService.error(`You must select at least a single question to publish/un-publish!`);
			return;
		}

		if (selectedIds.length > 5) {
			this.toastService.error(`You can only publish/unpublish 5 questions at a time!`);
			return;
		}

		this.diagnosticQuestionsStore.publishQuestions(selectedIds, release);
	}

	setQuestionTab(uid: string, tab: string) {
		this.selectedQuestionTab[uid] = tab;
	}

	getQuestionBlocks(question: DiagnosticQuestion): RichContentBlock[] {
		return question.level?.question?.blocks ?? [];
	}

	getOptionBlocks(option: QuizOptionBlock): RichContentBlock[] {
		return option.content?.blocks ?? [];
	}

	getPrimaryOptionBlockType(option: QuizOptionBlock): RichContentBlock['type'] | undefined {
		return this.getOptionBlocks(option)[0]?.type;
	}

	resolveImageUrl(url: string | undefined | null): string {
		return url?.trim() ? url : this.fallbackImageUrl;
	}

	onImageError(event: Event): void {
		const img = event.target as HTMLImageElement;
		if (img.src !== this.fallbackImageUrl) {
			img.src = this.fallbackImageUrl;
		}
	}
}
